// React
import { Link } from "react-router-dom";
import { useEffect, useState, useRef, memo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

// Components
import FAQAccordian from "../components/FAQAccordian";
import HeroSponsorExamples from "../components/HeroSponsorExamples";
import HomeNicheSearch from "../components/HomeNicheSearch";
// import InteractiveDemo from "../components/InteractiveDemo"; // see SECTION 5 below
import { User } from "../types/User";
import config from '../config';

// Font Awesome Icons
import { faArrowRight, faCheckCircle, faDatabase, faRocket, faMagnifyingGlass, faChartBar, faBolt, faDollarSign, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Analytics
import { 
    trackPageView, 
    trackButtonClick, 
    trackContentInteraction, 
    trackUserJourney, 
    trackTimeOnPage
} from '../utils/analytics';
import { trackHomePageViewed } from '../utils/funnelTracking';
import { formatMarketingCount } from '../utils/formatMarketingCount';

// Styles
import '../css/pages/Subscribe.css';

interface HomeProps {
    isSubscribed: boolean,
    email: string,
    sponsorCount: number,
    newsletterCount: number,
    lastUpdated: string,
    user?: User
}



const Home = ({ isSubscribed, email, newsletterCount, sponsorCount }: HomeProps) => {
    const [liveSponsorCount, setLiveSponsorCount] = useState(sponsorCount);
    const [liveNewsletterCount, setLiveNewsletterCount] = useState(newsletterCount);

    // Sync when App-level db-info loads
    useEffect(() => {
        if (sponsorCount) setLiveSponsorCount(sponsorCount);
        if (newsletterCount) setLiveNewsletterCount(newsletterCount);
    }, [sponsorCount, newsletterCount]);

    // Refresh counts from GET /sponsors/db-info (public)
    useEffect(() => {
        let mounted = true;
        axios.get(`${config.backendUrl}sponsors/db-info`).then(res => {
            if (!mounted) return;
            if (res.data?.sponsors) setLiveSponsorCount(res.data.sponsors);
            if (res.data?.newsletters) setLiveNewsletterCount(res.data.newsletters);
        }).catch(() => { /* keep App-provided or fallback values */ });
        return () => { mounted = false; };
    }, []);

    // Never fall through to 0 here — App.tsx's own dbInfo default/error-fallback
    // already floors these, but keep a matching floor in case Home ever
    // receives sponsorCount/newsletterCount as 0 directly (e.g. in isolation).
    const safeSponsorCount = liveSponsorCount || sponsorCount || 400;
    const safeNewsletterCount = liveNewsletterCount || newsletterCount || 100;
    const sponsorsDisplay = formatMarketingCount(safeSponsorCount);
    const newslettersDisplay = formatMarketingCount(safeNewsletterCount);
    
    // Time tracking
    const pageLoadTime = useRef(Date.now());
    const [timeOnPage, setTimeOnPage] = useState(0);

    // Scroll tracking
    const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

    // Track page view and user journey
    useEffect(() => {
        const startTime = pageLoadTime.current;
        
        trackPageView('Home', 'Find Newsletter Sponsors Fast');
        trackUserJourney('home_page_view', 1, { 
            sponsorCount: safeSponsorCount, 
            newsletterCount: safeNewsletterCount, 
            isSubscribed: isSubscribed ? 'yes' : 'no' 
        });
        // Funnel tracking
        trackHomePageViewed();

        // Track time on page
        const interval = setInterval(() => {
            const timeSpent = Math.floor((Date.now() - startTime) / 1000);
            setTimeOnPage(timeSpent);
            
            // Track time on page every 30 seconds
            if (timeSpent % 30 === 0 && timeSpent > 0) {
                trackTimeOnPage('Home', timeSpent);
            }
        }, 1000);

        // Track scroll behavior and section visibility
        const handleScroll = () => {
            const sections = ['hero', 'pricing', 'faq'];
            const newVisibleSections = new Set<string>();
            
            sections.forEach(sectionId => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
                    
                    if (isVisible && !visibleSections.has(sectionId)) {
                        newVisibleSections.add(sectionId);
                        trackContentInteraction('section_view', sectionId, 'visible', 'home');
                    }
                }
            });
            
            if (newVisibleSections.size > 0) {
                setVisibleSections(prev => new Set([...Array.from(prev), ...Array.from(newVisibleSections)]));
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial visibility

        return () => {
            clearInterval(interval);
            window.removeEventListener('scroll', handleScroll);
            
            // Track final time on page when component unmounts
            const finalTime = Math.floor((Date.now() - startTime) / 1000);
            if (finalTime > 10) { // Only track if user spent more than 10 seconds
                trackTimeOnPage('Home', finalTime);
            }
        };
    }, [safeSponsorCount, safeNewsletterCount, isSubscribed, visibleSections]);

    const handleSignupClick = useCallback((location: string) => {
        trackButtonClick('signup_cta', 'home', { 
            location, 
            time_on_page: timeOnPage,
            sponsor_count: safeSponsorCount 
        });
        trackUserJourney('signup_click', 2, { location, time_on_page: timeOnPage });
    }, [timeOnPage, safeSponsorCount]);

    const handlePricingClick = useCallback(() => {
        trackButtonClick('pricing_cta', 'home', {
            time_on_page: timeOnPage,
            sponsor_count: safeSponsorCount
        });
        trackUserJourney('pricing_click', 3, { time_on_page: timeOnPage });
    }, [timeOnPage, safeSponsorCount]);

    // Commented out until we have a working demo video
    // const handleVideoInteraction = useCallback((action: string) => {
    //     trackContentInteraction('demo_video', 'youtube_demo', action, 'home');
    // }, []);

    const organizationJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'SponsorDB',
        url: 'https://sponsor-db.com',
        description: 'A verified, constantly-growing database of real companies sponsoring newsletters, with direct contact info.',
        email: 'info@sponsor-db.com',
    };

    return (
        <div className="web-page">
            <Helmet>
                <link rel="canonical" href="https://sponsor-db.com/" />
                <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
            </Helmet>
            {/* SECTION 1: HERO */}
            <div className="web-section web-section-dark mt-0 hero-new" id="hero">
                <div className="hero-new-container">
                    <div className="hero-new-grid">
                        {/* Left Column - Text */}
                        <div className="hero-new-text">
                            <h1 className="hero-new-title">
                                Find companies already paying newsletters like yours.
                            </h1>
                            <p className="hero-new-subtitle">
                                Search {sponsorsDisplay} verified sponsorship opportunities, find relevant contacts, and reach out in seconds.
                            </p>
                            
                            <div className="hero-new-cta">
                                <Link to="/signup" className="hero-new-primary-btn" onClick={() => handleSignupClick('hero')}>
                                    Get SponsorDB — $20/month
                                    <FontAwesomeIcon icon={faArrowRight} className="hero-btn-arrow" />
                                </Link>
                                <p className="hero-new-secondary-text">Perfect for independent newsletter creators ready to monetize</p>
                            </div>
                        </div>
                        
                        {/* Right Column - Example sponsor contacts. A live
                            screenshot goes stale every time the product UI
                            changes; these cards show the actual thing a
                            subscriber gets (a real name, title, and email)
                            at a glance, and never need re-capturing. */}
                        <div className="hero-new-demo">
                            <HeroSponsorExamples />
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 2: PROBLEM STATEMENT */}
            <div className="web-section problem-statement-section" id="problem">
                <div className="web-section__container web-section-content">
                    <div className="problem-statement-container">
                        <p className="problem-statement-eyebrow">Sponsor outreach is broken</p>
                        <h2 className="problem-statement-title">Stop wasting hours on sponsor research</h2>
                        <p className="problem-statement-body">
                            Most newsletter creators still track sponsors by hand, scraping newsletters one by one, buying outdated lists, and guessing who to contact. It's slow, inaccurate, and frustrating. SponsorDB fixes that with an automatically growing database of verified sponsors with direct contact info.
                        </p>
                    </div>
                </div>
            </div>

            {/* SECTION 2.5: FREE NICHE SEARCH — real data, no signup, proves the
                database is real before asking for anything. */}
            <div className="web-section niche-search-section" id="search-by-niche">
                <div className="web-section__container web-section-content">
                    <div className="niche-search-container">
                        <p className="niche-search-eyebrow">Try it live</p>
                        <h2 className="niche-search-title">Search real sponsors by niche</h2>
                        <p className="niche-search-subtitle">
                            Pick your niche — see the actual companies sponsoring newsletters like yours, no account needed.
                        </p>
                        <HomeNicheSearch />
                    </div>
                </div>
            </div>

            {/* SECTION 2.75: TRUST BANNER — right after the free niche search
                proves the database is real, so "trusted by creators" lands
                while that's still fresh instead of much later before pricing. */}
            <div className="web-section trust-banner-section" id="trust">
                <div className="web-section__container web-section-content">
                    <div className="trust-banner-container">
                        <h2 className="trust-banner-title">Trusted by independent newsletter creators</h2>
                        <p className="trust-banner-subtitle">
                            Solo operators and small teams use SponsorDB to find sponsors, send pitches, and land their first deals without an agency or sales team.
                        </p>
                        <div className="home__inline-cta">
                            <Link to="/signup" className="hero-new-primary-btn" onClick={() => handleSignupClick('after-preview')}>
                                Unlock the full database <FontAwesomeIcon icon={faArrowRight} className="hero-btn-arrow" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 3: WHAT YOU GET */}
            <div className="web-section what-you-get-section" id="what-you-get">
                <div className="web-section__container web-section-content">
                    <div className="what-you-get-container">
                        <p className="what-you-get-eyebrow">What you get</p>
                        <h2 className="what-you-get-title">The fastest-growing newsletter sponsor database</h2>
                        
                        {/* Stats */}
                        <div className="what-you-get-stats what-you-get-stats--two">
                            <div className="what-you-get-stat-card">
                                <div className="what-you-get-stat-number">{sponsorsDisplay}</div>
                                <div className="what-you-get-stat-label">Verified Sponsors found in {newslettersDisplay} Newsletters</div>
                            </div>
                            <div className="what-you-get-stat-card">
                                <div className="what-you-get-stat-number">10+</div>
                                <div className="what-you-get-stat-label">New Sponsors Added Weekly</div>
                            </div>
                        </div>

                        {/* Feature Tags */}
                        <div className="what-you-get-tags">
                            <div className="what-you-get-tag">
                                <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                <span>Automatically growing database</span>
                            </div>
                            <div className="what-you-get-tag">
                                <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                <span>Verified contact info</span>
                            </div>
                            <div className="what-you-get-tag">
                                <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                <span>AI-powered matching</span>
                            </div>
                            <div className="what-you-get-tag">
                                <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                <span>One-click outreach</span>
                            </div>
                            <div className="what-you-get-tag">
                                <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                <span>Revenue tracking</span>
                            </div>
                            <div className="what-you-get-tag">
                                <FontAwesomeIcon icon={faCheckCircle} className="what-you-get-tag-icon" />
                                <span>Weekly updates</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 4: PROCESS */}
            <div className="web-section process-section" id="process">
                <div className="web-section__container web-section-content">
                    <div className="process-container">
                        <h2 className="process-title">From discover to outreach</h2>
                        <p className="process-subtitle">Your end-to-end sponsor workflow</p>

                        <div className="process-steps">
                            <div className="process-step-card">
                                <div className="process-step-header">
                                    <div className="process-step-number">1</div>
                                    <div className="process-step-icon">
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                    </div>
                                </div>
                                <h3 className="process-step-title">Find sponsors in your niche</h3>
                                <p className="process-step-description">
                                    Browse {sponsorsDisplay} verified sponsors or let AI match you with the best fits based on your newsletter topic and audience.
                                </p>
                            </div>

                            <div className="process-step-card">
                                <div className="process-step-header">
                                    <div className="process-step-number">2</div>
                                    <div className="process-step-icon">
                                        <FontAwesomeIcon icon={faBolt} />
                                    </div>
                                </div>
                                <h3 className="process-step-title">Send personalized pitches instantly</h3>
                                <p className="process-step-description">
                                    Generate pitch-ready emails in one click. We pre-fill everything based on the sponsor and your newsletter.
                                </p>
                            </div>

                            <div className="process-step-card">
                                <div className="process-step-header">
                                    <div className="process-step-number">3</div>
                                    <div className="process-step-icon">
                                        <FontAwesomeIcon icon={faDollarSign} />
                                    </div>
                                </div>
                                <h3 className="process-step-title">Track and close deals</h3>
                                <p className="process-step-description">
                                    Monitor every outreach, track response rates, and log revenue. Know exactly what's working.
                                </p>
                            </div>
                        </div>

                        <div className="home__inline-cta">
                            <Link to="/signup" className="hero-new-primary-btn" onClick={() => handleSignupClick('process')}>
                                Get SponsorDB <FontAwesomeIcon icon={faArrowRight} className="hero-btn-arrow" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 5: LIVE PRODUCT WALKTHROUGH — commented out, not
                deleted. This scripted mockup (search → filter → AI Pitch →
                drafted email) was standing in for a real demo of the
                database; now that HomeNicheSearch/PublicSponsorPreviewGrid
                shows the actual live database above, the mockup is
                redundant with something real just a scroll away. Re-enable
                by uncommenting this block and the InteractiveDemo import
                above if that changes.
            <div className="web-section demo-large-section" id="demo">
                <div className="web-section__container web-section-content">
                    <div className="demo-large-container">
                        <p className="demo-large-eyebrow">See SponsorDB in action</p>
                        <h2 className="demo-large-title">Everything you need to land sponsors, in one place</h2>

                        <InteractiveDemo />
                    </div>
                </div>
            </div>
            */}

            {/* SECTION 6: FEATURE CARDS */}
            <div className="web-section feature-cards-section" id="features">
                <div className="web-section__container web-section-content">
                    <div className="feature-cards-container">
                        <h2 className="feature-cards-title">Built for independent creators who want to move fast</h2>
                        <p className="feature-cards-subtitle">SponsorDB replaces spreadsheets and guesswork — built for independent newsletter creators with 1K–100K subscribers.</p>

                        <div className="feature-cards-grid">
                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faDatabase} />
                                </div>
                                <h3 className="feature-card-title">Verified Sponsor Database</h3>
                                <p className="feature-card-description">
                                    {sponsorsDisplay} companies proven to pay for newsletter placements — each vetted by AI against real newsletter evidence, with contact emails verified deliverable.
                                </p>
                            </div>

                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faRotateRight} />
                                </div>
                                <h3 className="feature-card-title">Database Grows Itself</h3>
                                <p className="feature-card-description">
                                    AI adds new sponsors every week automatically. Fresh opportunities keep landing in your database with zero effort from you — your list grows while you focus on writing.
                                </p>
                            </div>

                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faRocket} />
                                </div>
                                <h3 className="feature-card-title">Filter by Newsletter Size</h3>
                                <p className="feature-card-description">
                                    See only sponsors that already buy placements in newsletters your size — a sponsor of 500k-reader newsletters won't answer a 5k pitch, so we don't waste your time.
                                </p>
                            </div>

                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faBolt} />
                                </div>
                                <h3 className="feature-card-title">One-Click Outreach</h3>
                                <p className="feature-card-description">
                                    One click writes a personalized pitch that references the sponsor's actual past placements ("I saw you sponsored…") — copied to your clipboard and ready to send.
                                </p>
                            </div>

                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faChartBar} />
                                </div>
                                <h3 className="feature-card-title">Know What to Charge</h3>
                                <p className="feature-card-description">
                                    Built-in rate guidance based on your audience size, plus a simple pipeline to track who you've contacted, who replied, and which deals you won.
                                </p>
                            </div>

                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                </div>
                                <h3 className="feature-card-title">No Middlemen</h3>
                                <p className="feature-card-description">
                                    You own every relationship. No revenue sharing, no commission structures, no agencies taking a cut.
                                </p>
                            </div>

                            <div className="feature-card-dark">
                                <div className="feature-card-icon">
                                    <FontAwesomeIcon icon={faRocket} />
                                </div>
                                <h3 className="feature-card-title">Direct to Decision Makers</h3>
                                <p className="feature-card-description">
                                    Every contact is a verified marketing, partnership, or growth lead. No generic info@ emails, no contact forms, no dead ends.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 6.5: ROI — a small splitter making the economics
                explicit right before the trust/pricing push. Deliberately
                framed as "the subscription is cheap relative to one deal",
                not a claim about what any given user will actually earn. */}
            <div className="web-section roi-section" id="roi">
                <div className="web-section__container web-section-content">
                    <div className="roi-container">
                        <p className="roi-eyebrow">The Math</p>
                        <h2 className="roi-title">One sponsorship can pay for months of SponsorDB.</h2>
                        <p className="roi-body">
                            SponsorDB costs $20/month. Find one relevant sponsor and the subscription can pay for itself many times over.
                        </p>
                    </div>
                </div>
            </div>

            {/* SECTION 8: PRICING */}
            <div className="web-section pricing-new-section" id="pricing">
                <div className="web-section__container web-section-content">
                    <div className="subscribe-container">
                        <div className="subscribe-header">
                            <h1 className="subscribe-title">
                                Get instant access to SponsorDB
                            </h1>
                            <p className="subscribe-subtitle">
                                Built for independent newsletter creators with 1K–100K subscribers.
                            </p>
                        </div>

                        <div className="sdb-pricing">
                            <div className="sdb-pricing-card">
                                <div className="sdb-pricing-card__badge">Cancel Anytime</div>

                                <div className="sdb-pricing-card__header">
                                    <h3 className="sdb-pricing-card__title">Sponsor Access</h3>
                                    <div className="sdb-pricing-card__price">
                                        <span className="sdb-pricing-card__currency">$</span>
                                        <span className="sdb-pricing-card__amount">20</span>
                                        <span className="sdb-pricing-card__period">/month</span>
                                    </div>
                                </div>

                                <ul className="sdb-pricing-card__benefits">
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Access to {sponsorsDisplay} verified sponsors, not a preview — the full database</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Direct contact emails to real decision makers, not generic inboxes</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>AI-drafted, personalized pitch emails for every sponsor</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Smart sponsor matching based on your newsletter's niche and audience</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Filter by industry, audience size, and newsletter fit</span>
                                    </li>
                                    <li>
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Track your outreach — contacted, responded, closed</span>
                                    </li>
                                </ul>

                                <Link
                                    className="btn btn-primary btn-lg btn-full sdb-pricing-card__cta"
                                    to="/signup"
                                    onClick={handlePricingClick}
                                >
                                    Start finding sponsors <FontAwesomeIcon icon={faArrowRight} className="hero-btn-arrow" />
                                </Link>
                                <p className="sdb-pricing-card__note">Billed monthly • Cancel anytime</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 9: FAQ */}
            <div className="web-section web-section__container web-section-content" id="FAQ">
                <h2 className="web-section__container-header-sm">
                    Frequently Asked Questions
                </h2>
                <FAQAccordian />
            </div>
            <Link to="/signup" className="hero-new-primary-btn home__faq-cta" onClick={() => handleSignupClick('bottom')}>
                Get SponsorDB
                <FontAwesomeIcon icon={faArrowRight} className="hero-btn-arrow" />
            </Link>
        </div>
    );
};

export default memo(Home);


